import { PlannedTokenSettings } from "./remaining-settings-sections";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Credential,
  CredentialPurpose,
  DeviceConnection,
  ProjectRole,
} from "@zworks/console-contracts";
import { KeyRound, LockKeyhole, Plus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  ApiError,
  approveDevice,
  createCredential,
  credentialsQuery,
  devicesQuery,
  revokeDevice,
  rotateCredential,
} from "@/lib/api";

const purposes = {
  "read-repository": "Read-only repository",
  "write-repository": "Repository writes",
  "runtime-service": "Runtime service",
  "model-key": "Model API key (BYOK)",
};
function CredentialStatus({ credential }: { credential: Credential }) {
  const [now] = useState(() => Date.now());
  if (!credential.valid) return <Badge variant="danger">Not connected</Badge>;
  if (credential.expiresAt) {
    const days = Math.ceil((Date.parse(credential.expiresAt) - now) / 86_400_000);
    if (days <= 14)
      return <Badge variant="warning">{days <= 0 ? "Expired" : `Expires in ${days} days`}</Badge>;
  }
  return <Badge variant="success">Valid</Badge>;
}

function RotateDialog({
  projectId,
  credential,
  close,
  done,
}: {
  projectId: string;
  credential: Credential;
  close: () => void;
  done: (message: string) => void;
}) {
  const [value, setValue] = useState("");
  const [expiry, setExpiry] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!value || saving) return;
    setSaving(true);
    setFailure(null);
    const material = value;
    // Clear the DOM before the request; failures never echo material or preserve it for retry.
    setValue("");
    try {
      await rotateCredential(projectId, credential.ref, {
        value: material,
        expiresAt: expiry ? new Date(`${expiry}T23:59:59Z`).toISOString() : null,
      });
      done("Credential updated.");
      close();
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? (error.problem?.detail ?? error.message)
          : "The credential could not be updated.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) close();
      }}
    >
      <DialogContent>
        <DialogTitle>{credential.valid ? "Rotate credential" : "Connect credential"}</DialogTitle>
        <DialogDescription>
          Replace the material for {credential.ref}. The previous value stops working in this
          project. Values are never shown.
        </DialogDescription>
        <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
          <label className="text-sm font-medium">
            Replacement value
            <Input
              type="password"
              autoComplete="new-password"
              maxLength={65536}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={saving}
            />
          </label>
          <label className="text-sm font-medium">
            Expiry date (UTC, optional)
            <Input
              type="date"
              min={new Date().toISOString().slice(0, 10)}
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              disabled={saving}
            />
          </label>
          {failure ? (
            <p role="alert" className="text-sm text-danger">
              {failure}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" type="button" disabled={saving} onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={!value || saving}>
              {saving ? "Saving…" : "Save credential"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({
  projectId,
  close,
  done,
}: {
  projectId: string;
  close: () => void;
  done: (message: string) => void;
}) {
  const [ref, setRef] = useState("");
  const [purpose, setPurpose] = useState<CredentialPurpose>("read-repository");
  const [value, setValue] = useState("");
  const [expiry, setExpiry] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ref.trim() || !value || saving) return;
    setSaving(true);
    setFailure(null);
    const material = value;
    setValue("");
    try {
      await createCredential(projectId, {
        ref: ref.trim(),
        purpose,
        value: material,
        expiresAt: expiry ? new Date(`${expiry}T23:59:59Z`).toISOString() : null,
      });
      done("Credential connected.");
      close();
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? (error.problem?.detail ?? error.message)
          : "The credential could not be connected.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) close();
      }}
    >
      <DialogContent>
        <DialogTitle>Connect a credential</DialogTitle>
        <DialogDescription>
          Create a project reference and add its value once. The value is encrypted for storage and
          never shown again.
        </DialogDescription>
        <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
          <label className="text-sm font-medium">
            Reference
            <Input
              required
              maxLength={200}
              value={ref}
              onChange={(event) => setRef(event.target.value)}
              disabled={saving}
            />
          </label>
          <label className="text-sm font-medium">
            Purpose
            <select
              className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={purpose}
              onChange={(event) => setPurpose(event.target.value as CredentialPurpose)}
              disabled={saving}
            >
              <option value="read-repository">Read-only repository</option>
              <option value="write-repository">Repository writes</option>
              <option value="runtime-service">Runtime service</option>
              <option value="model-key">Model API key (BYOK)</option>
            </select>
          </label>
          <label className="text-sm font-medium">
            Credential value
            <Input
              required
              type="password"
              autoComplete="new-password"
              maxLength={65536}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              disabled={saving}
            />
          </label>
          <label className="text-sm font-medium">
            Expiry date (UTC, optional)
            <Input
              type="date"
              min={new Date().toISOString().slice(0, 10)}
              value={expiry}
              onChange={(event) => setExpiry(event.target.value)}
              disabled={saving}
            />
          </label>
          {failure ? (
            <p role="alert" className="text-sm text-danger">
              {failure}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" type="button" disabled={saving} onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ref.trim() || !value || saving}>
              {saving ? "Saving…" : "Save credential"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Reference-only settings; token issuance belongs to the initiating CLI, never this page. */
export function CredentialsSection({ projectId, role }: { projectId: string; role: ProjectRole }) {
  const queryClient = useQueryClient();
  const credentials = useQuery(credentialsQuery(projectId));
  const devices = useQuery(devicesQuery(projectId));
  const [rotating, setRotating] = useState<Credential | null>(null);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<DeviceConnection | null>(null);
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const canManage = role === "owner";
  const error = credentials.error ?? devices.error ?? failure;
  const forbidden = error instanceof ApiError && (error.status === 403 || error.status === 404);
  async function refresh(message: string) {
    setNotice(message);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "credentials"] }),
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "devices"] }),
    ]);
  }
  async function act(work: () => Promise<unknown>, message: string) {
    if (saving) return;
    setSaving(true);
    setFailure(null);
    setNotice(null);
    try {
      await work();
      setCode("");
      setRevoking(null);
      await refresh(message);
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error
          : new ApiError("The request could not be completed.", 500),
      );
    } finally {
      setSaving(false);
    }
  }
  if (forbidden)
    return (
      <ForbiddenState
        title="Credentials are hidden"
        description="Only project members can view credentials and connections."
      />
    );
  if (credentials.isPending || devices.isPending)
    return <LoadingState label="Loading credentials" rows={4} />;
  if (credentials.isError || devices.isError)
    return (
      <ErrorState
        title="Credentials could not be loaded"
        description="The request could not be completed."
        requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
        onRetry={() => {
          void credentials.refetch();
          void devices.refetch();
        }}
      />
    );
  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Credentials</h2>
        <div className="flex items-center gap-3">
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <LockKeyhole className="size-4" aria-hidden />
            Values are never shown
          </p>
          {canManage ? (
            <Button
              onClick={() => {
                setNotice(null);
                setCreating(true);
              }}
            >
              <Plus className="size-4" aria-hidden />
              Connect credential
            </Button>
          ) : null}
        </div>
      </header>
      <PlannedTokenSettings />
      {notice ? (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      ) : null}
      {failure ? (
        <p role="alert" className="text-sm text-danger">
          {failure.problem?.detail ?? failure.message}
          {failure.requestId ? ` Reference ${failure.requestId}.` : ""}
        </p>
      ) : null}
      <section aria-label="Project credentials">
        <h3 className="text-sm text-muted-foreground">Project credentials</h3>
        {!credentials.data?.length ? (
          <EmptyState
            title="No credentials connected"
            description="Connect a credential reference to this project."
          />
        ) : (
          credentials.data.map((credential) => (
            <article
              key={credential.id}
              aria-label={credential.ref}
              className="flex flex-wrap items-center gap-3 border-b py-4"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{purposes[credential.purpose]}</p>
                <p className="text-sm text-muted-foreground">
                  Reference <span className="font-mono">{credential.ref}</span>
                </p>
                {credential.expiresAt ? (
                  <p className="text-xs text-muted-foreground">
                    Expires {formatDate(credential.expiresAt)} (UTC)
                  </p>
                ) : null}
              </div>
              <CredentialStatus credential={credential} />
              {canManage ? (
                <Button
                  variant="outline"
                  aria-label={`${credential.valid ? "Rotate" : credential.rotatedAt ? "Reconnect" : "Connect"} ${credential.ref}`}
                  onClick={() => {
                    setNotice(null);
                    setRotating(credential);
                  }}
                >
                  {credential.valid ? "Rotate" : credential.rotatedAt ? "Reconnect" : "Connect"}
                </Button>
              ) : null}
            </article>
          ))
        )}
      </section>
      <section aria-label="Command line and tools" className="flex flex-col gap-4">
        <h3 className="text-sm text-muted-foreground">Command line and tools</h3>
        {canManage ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (code.trim())
                void act(() => approveDevice(projectId, code.trim()), "Client approved.");
            }}
            className="flex flex-col gap-3 border-b pb-4"
          >
            <div>
              <p className="font-medium">Connect a client</p>
              <p className="text-sm text-muted-foreground">
                Run <code>zworks login</code> in your terminal, then enter its code. Only approve a
                client you started. Its token is never displayed here.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm">
                Device code
                <Input
                  autoComplete="off"
                  maxLength={200}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  disabled={saving}
                />
              </label>
              <Button type="submit" aria-label="Approve client" disabled={!code.trim() || saving}>
                {saving ? "Approving…" : "Approve"}
              </Button>
            </div>
          </form>
        ) : null}
        {!devices.data?.length ? (
          <p className="text-sm text-muted-foreground">No clients connected.</p>
        ) : (
          devices.data.map((device) => (
            <article
              key={device.id}
              aria-label={device.client}
              className="flex flex-wrap items-center gap-3 border-b py-3"
            >
              <KeyRound className="size-5 text-muted-foreground" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{device.client}</p>
                <p className="text-sm text-muted-foreground">
                  {device.lastUsedAt
                    ? `Last used ${formatDate(device.lastUsedAt)} (UTC)`
                    : "Not used yet"}
                </p>
              </div>
              <Badge variant={device.revokedAt ? "danger" : "success"}>
                {device.revokedAt ? "Revoked" : "Connected"}
              </Badge>
              {canManage && !device.revokedAt ? (
                <Button
                  variant="outline"
                  aria-label={`Revoke ${device.client}`}
                  onClick={() => setRevoking(device)}
                >
                  Revoke
                </Button>
              ) : null}
            </article>
          ))
        )}
      </section>
      {rotating ? (
        <RotateDialog
          key={rotating.id}
          projectId={projectId}
          credential={rotating}
          close={() => setRotating(null)}
          done={(message) => void refresh(message)}
        />
      ) : null}
      {creating ? (
        <CreateDialog
          projectId={projectId}
          close={() => setCreating(false)}
          done={(message) => void refresh(message)}
        />
      ) : null}
      {revoking ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open && !saving) setRevoking(null);
          }}
        >
          <DialogContent>
            <DialogTitle>Revoke connection</DialogTitle>
            <DialogDescription>
              {revoking.client} will lose access on its next call.
            </DialogDescription>
            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={saving} onClick={() => setRevoking(null)}>
                Cancel
              </Button>
              <Button
                disabled={saving}
                onClick={() =>
                  void act(() => revokeDevice(projectId, revoking.id), "Client revoked.")
                }
              >
                {saving ? "Revoking…" : "Revoke connection"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
